import React from 'react';
import { formatLapTime } from '../utils/format';

const styles = {
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  headerCard: {
    background: 'linear-gradient(135deg, rgba(16, 34, 58, 0.96), rgba(12, 22, 37, 0.96))',
    border: '1px solid rgba(102, 153, 204, 0.18)',
    borderRadius: 10,
    padding: '16px 18px',
  },
  headerTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 16,
    flexWrap: 'wrap',
  },
  headerTitle: {
    fontFamily: 'var(--font-heading)',
    fontSize: 16,
    fontWeight: 700,
    color: '#f5f7fa',
    letterSpacing: 1.2,
  },
  headerMeta: {
    marginTop: 6,
    fontFamily: 'var(--font-body)',
    fontSize: 13,
    color: 'rgba(226, 232, 240, 0.78)',
    lineHeight: 1.5,
  },
  headerStat: {
    textAlign: 'right',
  },
  headerStatLabel: {
    fontFamily: 'var(--font-heading)',
    fontSize: 10,
    color: 'rgba(148, 163, 184, 0.88)',
    letterSpacing: 1.4,
    textTransform: 'uppercase',
  },
  headerStatValue: {
    marginTop: 6,
    fontFamily: 'var(--font-data)',
    fontSize: 22,
    color: '#7dd3fc',
  },
  legendRow: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
    marginTop: 14,
  },
  legendChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 10px',
    borderRadius: 999,
    background: 'rgba(255, 255, 255, 0.06)',
    color: '#dbe4ee',
    fontFamily: 'var(--font-body)',
    fontSize: 12,
  },
  legendSwatch: {
    width: 12,
    height: 12,
    borderRadius: 3,
    flexShrink: 0,
  },
  card: {
    background: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: 10,
    overflowX: 'auto',
  },
  empty: {
    padding: '18px 20px',
    color: 'var(--color-text-dim)',
    fontFamily: 'var(--font-body)',
    fontSize: 14,
    border: '1px solid var(--color-border)',
    borderRadius: 10,
    overflow: 'hidden',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: 760,
  },
  th: {
    padding: '12px 14px',
    background: 'var(--color-surface-light)',
    borderBottom: '1px solid var(--color-border)',
    color: 'var(--color-text-dim)',
    fontFamily: 'var(--font-heading)',
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 1.4,
    textTransform: 'uppercase',
    textAlign: 'left',
  },
  td: {
    padding: '12px 14px',
    borderBottom: '1px solid var(--color-border)',
    color: 'var(--color-text)',
    fontFamily: 'var(--font-data)',
    fontSize: 13,
    textAlign: 'right',
    whiteSpace: 'nowrap',
  },
  labelCell: {
    minWidth: 110,
    position: 'sticky',
    left: 0,
    background: 'var(--color-surface)',
    zIndex: 1,
  },
  labelHeader: {
    position: 'sticky',
    left: 0,
    zIndex: 2,
  },
  colorSwatch: {
    width: 10,
    height: 10,
    borderRadius: 999,
    flexShrink: 0,
  },
  bold: {
    fontWeight: 700,
  },
  totalRow: {
    background: 'var(--color-surface-light)',
  },
  summaryRow: {
    background: 'rgba(125, 211, 252, 0.08)',
  },
};

function formatSplitMs(timeMs) {
  if (timeMs == null) return '--';
  return formatLapTime(timeMs / 1000);
}

function getBestSegmentStyle(entry) {
  if (!entry) return {};
  if (entry.isBestForSegment) return { background: 'rgba(46, 204, 113, 0.25)', color: 'var(--color-accent)' };
  return {};
}

export default function SplitTimeTable({ report }) {
  if (!report?.segments?.length || !report?.laps?.length) {
    return <div style={styles.empty}>No split-time data available for this track/session yet.</div>;
  }

  const entriesByKey = new Map(
    report.entries.map((entry) => [`${entry.lapKey}-${entry.segmentIndex}`, entry])
  );
  const eclecticBySegment = new Map(
    report.eclectic.map((entry) => [entry.segmentIndex, entry])
  );
  const rollingBySegment = new Map(
    (report.rollingMinimum?.entries || []).map((entry) => [entry.segmentIndex, entry])
  );
  const lapTotalsByKey = new Map(
    report.lapTotals.map((item) => [item.lapKey || `${item.lap}`, item.totalMs])
  );
  const lapInfo = report.lapInfo || new Map();
  const uniqueDrivers = new Set([...lapInfo.values()].map((info) => info.driverLabel).filter(Boolean));
  const isCompareMode = uniqueDrivers.size > 1;
  const bestLapEntry = report.lapTotals.find((t) => t.lap === report.bestLapNumber);
  const bestLapKey = bestLapEntry?.lapKey || `${report.bestLapNumber}`;
  const bestLapTotal = lapTotalsByKey.get(bestLapKey);
  const bestLapDriver = lapInfo.get(bestLapKey)?.driverLabel;

  return (
    <div style={styles.wrapper}>
      <div style={styles.headerCard}>
        <div style={styles.headerTop}>
          <div>
            <div style={styles.headerTitle}>Split Times</div>
            <div style={styles.headerMeta}>
              {report.trackName || 'Track'} | {report.laps.length} laps | {report.segments.length} sections
              {report.referenceLapNumber != null ? ` | Reference lap ${report.referenceLapNumber}` : ''}
              {report.rollingMinimum?.startLap != null ? ` | Rolling min starts lap ${report.rollingMinimum.startLap}` : ''}
            </div>
          </div>
          <div style={styles.headerStat}>
            <div style={styles.headerStatLabel}>Best Full Lap</div>
            <div style={styles.headerStatValue}>
              {report.bestLapNumber != null
                ? `${isCompareMode && bestLapDriver ? `${bestLapDriver} ` : ''}Lap ${report.bestLapNumber}${bestLapTotal != null ? `: ${formatSplitMs(bestLapTotal)}` : ''}`
                : '--'}
            </div>
          </div>
        </div>
        <div style={styles.legendRow}>
          <div style={styles.legendChip}>
            <span style={{ ...styles.legendSwatch, background: 'var(--color-accent)' }} />
            * Best for section
          </div>
          <div style={styles.legendChip}><strong>Bold</strong> rolling minimum</div>
        </div>
      </div>

      <div style={styles.card}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={{ ...styles.th, ...styles.labelHeader }}>Lap</th>
              {report.segments.map((segment) => (
                <th key={segment.index} style={styles.th}>
                  {segment.label}
                </th>
              ))}
              <th style={styles.th}>Total</th>
            </tr>
          </thead>
          <tbody>
            {report.laps.map((lapKey) => {
              const info = lapInfo.get(lapKey);
              const lapNum = info?.lapNum ?? lapKey;
              const driver = info?.driverLabel;
              const isBest = lapNum === report.bestLapNumber;
              const label = isCompareMode && driver
                ? `${driver} L${lapNum}`
                : `Lap ${lapNum}`;
              return (
                <tr key={lapKey}>
                  <td style={{ ...styles.td, ...styles.labelCell, textAlign: 'left', fontWeight: isBest ? 700 : 500 }}>
                    {isBest ? `${label} *` : label}
                  </td>
                  {report.segments.map((segment) => {
                    const entry = entriesByKey.get(`${lapKey}-${segment.index}`);
                    return (
                      <td
                        key={`${lapKey}-${segment.index}`}
                        style={{
                          ...styles.td,
                          ...(entry?.isRollingMinimum ? styles.bold : {}),
                          ...getBestSegmentStyle(entry),
                        }}
                      >
                        {entry ? `${formatSplitMs(entry.sectionTimeMs)}${entry.isBestForSegment ? ' *' : ''}` : '--'}
                      </td>
                    );
                  })}
                  <td style={{ ...styles.td, ...styles.bold }}>
                    {formatSplitMs(lapTotalsByKey.get(lapKey))}
                  </td>
                </tr>
              );
            })}

            <tr style={styles.summaryRow}>
              <td style={{ ...styles.td, ...styles.labelCell, ...styles.bold, textAlign: 'left' }}>Eclectic</td>
              {report.segments.map((segment) => (
                <td key={`eclectic-${segment.index}`} style={{ ...styles.td, ...styles.bold }}>
                  {formatSplitMs(eclecticBySegment.get(segment.index)?.timeMs)}
                </td>
              ))}
              <td style={{ ...styles.td, ...styles.bold }}>{formatSplitMs(report.eclecticTotal)}</td>
            </tr>

            <tr style={styles.totalRow}>
              <td style={{ ...styles.td, ...styles.labelCell, ...styles.bold, textAlign: 'left' }}>
                Rolling Minimum
              </td>
              {report.segments.map((segment) => (
                <td key={`rolling-${segment.index}`} style={{ ...styles.td, ...styles.bold }}>
                  {formatSplitMs(rollingBySegment.get(segment.index)?.sectionTimeMs)}
                </td>
              ))}
              <td style={{ ...styles.td, ...styles.bold }}>{formatSplitMs(report.rollingMinimum?.totalMs)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
